import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import {
  Users,
  Storefront,
  QrCode,
  Spinner,
  Gift,
  CheckCircle,
  TrendUp,
  ArrowRight,
  Clock
} from "@phosphor-icons/react";

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="card-pop p-4 flex items-start gap-4">
      <div
        className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}20`, color }}
      >
        <Icon size={20} weight="bold" />
      </div>
      <div>
        <div className="text-2xl font-black">{value}</div>
        <div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function MiniChart({ data, label }) {
  const max = Math.max(...Object.values(data), 1);
  const entries = Object.entries(data).slice(-7); // last 7 days

  return (
    <div className="card-pop p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm">{label}</h3>
      </div>
      <div className="flex items-end gap-1 h-24">
        {entries.map(([day, val]) => (
          <div key={day} className="flex-1 flex flex-col items-center gap-1 group">
            <div
              className="w-full rounded-sm bg-orange-500/80 group-hover:bg-orange-600 transition-all"
              style={{ height: `${(val / max) * 100}%` }}
            />
            <span className="text-[10px] text-[var(--ink-muted)] rotate-0">{day.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityRow({ icon: Icon, text, time }) {
  return (
    <div className="flex items-start gap-3 py-2 border-b" style={{ borderColor: "var(--border)" }}>
      <div className="mt-0.5">
        <Icon size={16} className="text-[var(--ink-muted)]" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{text}</p>
        <p className="text-xs text-[var(--ink-muted)] flex items-center gap-1">
          <Clock size={12} /> {time}
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [topOwners, setTopOwners] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
    fetchTopOwners();
  }, []);

  async function fetchStats() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) throw new Error("Failed to load stats");
      const data = await res.json();
      setStats(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRecentActivity() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/activity?limit=10`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setActivity(data.slice(0, 10));
    } catch {
      // silent
    }
  }

  async function fetchTopOwners() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/owners?limit=5`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTopOwners(data);
    } catch {
      // silent
    }
  }

  const fmtNum = (n) => (n ?? 0).toLocaleString();
  const fmtPct = (n) => `${n ?? 0}%`;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="tag">Loading platform stats...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Owners" value={fmtNum(stats?.total_owners)} icon={Users} color="#FF7A59" />
          <StatCard label="Active (30d)" value={fmtNum(stats?.active_owners_30d)} icon={Users} color="#34C759" />
          <StatCard label="Total Outlets" value={fmtNum(stats?.total_outlets)} icon={Storefront} color="#5856D6" />
          <StatCard label="Total Scans" value={fmtNum(stats?.total_scans)} icon={QrCode} color="#FF9500" />
          <StatCard label="Total Spins" value={fmtNum(stats?.total_spins)} icon={Spinner} color="#007AFF" />
          <StatCard label="Rewards Won" value={fmtNum(stats?.total_wins)} icon={Gift} color="#AF52DE" />
          <StatCard label="Redemptions" value={fmtNum(stats?.total_redemptions)} icon={CheckCircle} color="#34C759" />
          <StatCard label="Conversion" value={fmtPct(stats?.conversion_rate)} icon={TrendUp} color="#FF2D55" />
        </div>

        {/* Charts + Side widgets */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            <MiniChart data={{}} label="Daily Scans (Coming Soon)" />
            <MiniChart data={{}} label="Daily Spins (Coming Soon)" />
          </div>

          <div className="space-y-4">
            {/* Recent Activity */}
            <div className="card-pop p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">Recent Activity</h3>
                <Link to="/admin/activity" className="text-xs font-bold text-orange-500 hover:underline">
                  View all <ArrowRight size={12} className="inline" />
                </Link>
              </div>
              <div className="space-y-0">
                {activity.length === 0 && (
                  <p className="text-sm text-[var(--ink-muted)] py-4 text-center">No recent activity</p>
                )}
                {activity.map((a, i) => (
                  <ActivityRow
                    key={i}
                    icon={a.event_type === "scan" ? QrCode : Gift}
                    text={`${a.event_type === "scan" ? "Scan" : "Spin"} at ${a.outlets?.name || "Unknown Outlet"}`}
                    time={new Date(a.created_at).toLocaleString()}
                  />
                ))}
              </div>
            </div>

            {/* Top Owners */}
            <div className="card-pop p-4">
              <h3 className="font-bold mb-3">Top Owners</h3>
              {topOwners.length === 0 ? (
                <p className="text-sm text-[var(--ink-muted)] py-4 text-center">No owners yet</p>
              ) : (
                <div className="space-y-2">
                  {topOwners.map((o) => (
                    <Link
                      key={o.id}
                      to={`/admin/owners/${o.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div>
                        <div className="text-sm font-bold">{o.name}</div>
                        <div className="text-xs text-[var(--ink-muted)]">{o.business_name}</div>
                      </div>
                      <div className="text-xs font-bold text-orange-500">{o.outlets?.[0]?.count || 0} outlets</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}